import React from 'react';
import {workdayLabelMap} from '@lib/logic';
import {Workday, WorkspaceReservation} from '@lib/store/src/data/timeslots/apiTypes';
import {timeLabel} from '@lib/store/src/data/timeslots/dateTimeHelpers';
import {isGroupBooking, isOfficeWorkday} from '@lib/store/src/data/timeslots/utils';
import {WorkdayHeaderTitle} from 'src/components/organisms/calendar/WorkdayTile/WorkdayTileHeader';
import {useTranslation} from 'react-i18next';
import {WorkdayCollapsibleTile} from 'src/components/organisms/calendar/WorkdayTile/WorkdayCollapsibleTile';
import {groupBy} from '@lib/utils';
import {TileMapPreview} from 'src/components/organisms/calendar/CalendarDayOverview/components/TileMapPreview';
import {Icon} from '@atoms';
import {
  ConnectionUser,
  ConnectWorkspaceReservation,
  getConnectsWorkspaceReservationsByUserId,
  useAppSelector,
  UserInfo,
} from '@lib/store';
import {WorkdayIcon} from '@molecules';

export const UserScheduleWorkdayTile = ({
  workday,
  initialOpen,
  user,
}: {
  workday: Workday;
  initialOpen?: boolean;
  user: UserInfo | ConnectionUser;
}) => {
  const {t} = useTranslation();

  const userWorkspaceReservations = useAppSelector((state) =>
    getConnectsWorkspaceReservationsByUserId(state, workday.ownerId),
  );

  const workdayReservations = userWorkspaceReservations?.filter((reservation) => reservation.workdayId === workday.id);

  const reservationsByNodeId = workdayReservations
    ? Object.entries(groupBy(workdayReservations, (r) => r.workspace.nodeId)).map(([nodeId, r]) => ({
        nodeId,
        reservations: r,
      }))
    : [];

  return (
    <div className="p-4 rounded-xl border border-gray-200 bg-beige-200">
      <div className="flex items-center p-1.5 gap-4 w-full">
        <WorkdayIcon
          status={workday?.status}
          className="bg-energizing-yellow-500*"
        />

        <div>
          {isOfficeWorkday(workday) ? (
            <WorkdayHeaderTitle
              title={workday.buildingName}
              subtitle={timeLabel(workday.localStart, workday.localEnd, t)}
            />
          ) : (
            <WorkdayHeaderTitle
              title={t(workdayLabelMap[workday.status])}
              subtitle={timeLabel(workday.localStart, workday.localEnd, t)}
            />
          )}
        </div>
      </div>
      {reservationsByNodeId.length > 0 && (
        <div className="mt-2 bg-white rounded-xl empty:mt-0">
          <WorkdayCollapsibleTile
            handle={t('timeslots:DeskBooking', {
              count: workdayReservations?.length || 0,
            })}
            icon="desk"
            initialOpen={initialOpen}
            contentClassName="p-0">
            <div className="min-h-[100px]">
              {reservationsByNodeId.map(({nodeId, reservations}) => (
                <div
                  key={nodeId}
                  className="divide-y">
                  {/* Grouped by reservation, take first */}
                  <div className="border-t [&_.MapiqMap-controls]:absolute">
                    <TileMapPreview workspaceReservation={reservations[0]} />
                  </div>

                  {reservations.map((workspaceReservation, index) => (
                    <WorkspaceReservationTile
                      key={workspaceReservation.id}
                      workspaceReservation={workspaceReservation}
                    />
                  ))}
                </div>
              ))}
            </div>
          </WorkdayCollapsibleTile>
        </div>
      )}
    </div>
  );
};

const WorkspaceReservationTile = ({workspaceReservation}: {workspaceReservation: ConnectWorkspaceReservation}) => {
  const {t} = useTranslation();
  const deskName = 'deskName' in workspaceReservation.workspace ? workspaceReservation.workspace.deskName : null;
  const areaName = 'areaName' in workspaceReservation.workspace ? workspaceReservation.workspace.areaName : null;
  const floorName = 'floorName' in workspaceReservation.workspace ? workspaceReservation.workspace.floorName : null;

  return (
    <div className={`flex flex-row gap-2 items-start px-5 py-5 text-left w-full`}>
      <Icon
        icon="desk"
        size="1.5rem"
      />

      <div className="ml-4 flex-1">
        {workspaceReservation?.invitationTo !== null ||
          (workspaceReservation?.hasInvitations === true && <p>{t('workplace:GroupBooking')}</p>)}

        <p className="mb-2 text-[15px] text-black">
          {timeLabel(workspaceReservation.localStart, workspaceReservation.localEnd, t)}
        </p>

        <p className="text-[15px] font-semibold text-black">{areaName}</p>

        {floorName && (
          <p className="text-[15px] font-semibold text-black">
            {floorName}
            {floorName && deskName && `, ${deskName}`}
          </p>
        )}
      </div>
    </div>
  );
};
